"use client";

import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { useEffect } from "react";

// Only add new errors to filter if there's a reference to a GitHub issue or Stack Overflow answer.
const errorPatternsToFilter = [
  "ResizeObserver loop completed with undelivered notifications.", // Common browser error, safe to ignore. https://github.com/DataDog/browser-sdk/issues/1616
  'Uncaught "Object Not Found Matching Id', // https://github.com/DataDog/browser-sdk/issues/2715
  "clarity.ms/collect", // We don't need to track clarity errors, we don't even use clarity. I don't know why they're showing up.
  "AbortError: Old view transition aborted by new view transition.", // View transition error, safe to ignore.
];

// Google Maps errors that originate inside Google's own script (maps.googleapis.com) — not our code.
// These are downgraded to warn logs. Reference: PLA-883
const googleMapsErrorPatterns = [
  "a.Oh.assign is not a function", // TypeError inside Google Maps SDK common.js, caused by rolling weekly API updates
];

const shouldFilterError = (message: string): boolean =>
  errorPatternsToFilter.some((pattern) => message.includes(pattern));

const isGoogleMapsError = (message: string): boolean =>
  googleMapsErrorPatterns.some((pattern) => message.includes(pattern));

// Global flag to prevent multiple initializations
let isInitialized = false;
const isBrowser = typeof window !== "undefined";

/**
 * Reset the Datadog session by stopping the current session and starting a new one.
 * Use this for long-running sessions (like kiosk) to prevent exorbitantly long replays.
 */
export function resetDatadogSession() {
  if (!isBrowser || !isInitialized) {
    return;
  }

  try {
    // Stop current session replay recording
    datadogRum.stopSessionReplayRecording();

    // Stop the current RUM session entirely - this creates a new session ID on next page activity
    datadogRum.stopSession();

    // Clear user context for fresh session
    datadogRum.clearUser();

    // Start fresh session replay recording
    datadogRum.startSessionReplayRecording();

    // Re-initialize empty user object
    datadogRum.setUser({
      id: "",
      email: "",
      orgId: "",
      practiceID: "",
    });

    console.log("[Datadog] Session reset - new session started");
  } catch (error) {
    console.warn("[Datadog] Failed to reset session:", error);
  }
}

export default function DatadogInit() {
  useEffect(() => {
    // Prevent multiple initializations
    if (
      isInitialized ||
      process.env.NEXT_PUBLIC_DATADOG_RUM_FLAG !== "true" ||
      !isBrowser
    ) {
      return;
    }

    isInitialized = true;

    const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const originRe =
      typeof window !== "undefined"
        ? new RegExp(`^${escapeRe(location.origin)}(/|$)`, "i")
        : /^$/; // no-op on SSR

    datadogRum.init({
      applicationId: `${process.env.NEXT_PUBLIC_DD_APPLICATIONID}`,
      clientToken: `${process.env.NEXT_PUBLIC_DD_CLIENTTOKEN}`,
      site: "us5.datadoghq.com",
      env:
        process.env.NEXT_PUBLIC_SAM_ENV === "production"
          ? "prod"
          : `${process.env.NEXT_PUBLIC_SAM_ENV}`,
      service: "sam",
      version: process.env.NEXT_PUBLIC_APP_VERSION,
      sessionSampleRate: 100,
      sessionReplaySampleRate: process.env
        .NEXT_PUBLIC_SESSION_REPLAY_SAMPLE_RATE
        ? Number.parseInt(process.env.NEXT_PUBLIC_SESSION_REPLAY_SAMPLE_RATE)
        : 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      startSessionReplayRecordingManually: true,
      allowedTracingUrls: [
        {
          match:
            /^(?!blob:)((https?:\/\/.*\.on-aptible\.com)|(https?:\/\/.*\.gosuperscript\.org)|(https?:\/\/.*\.superscript\.nyc))/gi,
          propagatorTypes: ["tracecontext", "b3multi", "b3", "datadog"],
        },
        {
          // always inject for current origin (preview/prod/local)
          match: originRe,
          propagatorTypes: ["tracecontext", "datadog", "b3multi", "b3"],
        },
      ],
      beforeSend: (event, context) => {
        if (event.type === "error" && shouldFilterError(event.error.message)) {
          return false;
        }
        if (event.type === "error" && isGoogleMapsError(event.error.message)) {
          datadogLogs.logger.warn(`[google-maps] ${event.error.message}`, {
            stack: event.error.stack,
          });
          return false;
        }
        return true;
      },
      enableExperimentalFeatures: ["clickmap"],
      trackViewsManually: false,
      traceContextInjection: "all",
      traceSampleRate: process.env.NEXT_PUBLIC_TRACE_SAMPLE_RATE
        ? Number.parseInt(process.env.NEXT_PUBLIC_TRACE_SAMPLE_RATE)
        : 100,
      storeContextsAcrossPages: true,
    });
    datadogRum.startSessionReplayRecording();
    // Initialize to an empty user object
    datadogRum.setUser({
      id: "",
      email: "",
      orgId: "",
      practiceID: "",
    });

    datadogLogs.init({
      clientToken: `${process.env.NEXT_PUBLIC_DD_LOGS_CLIENT_TOKEN}`,
      env:
        process.env.NEXT_PUBLIC_SAM_ENV === "production"
          ? "prod"
          : `${process.env.NEXT_PUBLIC_SAM_ENV}`,
      service: "sam",
      version: process.env.NEXT_PUBLIC_APP_VERSION,
      site: "us5.datadoghq.com",
      sessionSampleRate: 100,
      beforeSend(event, context) {
        if (event.type === "error" && shouldFilterError(event.message)) {
          return false;
        }
        return true;
      },
    });
  }, []);

  // Render nothing - this component is only included so that the init code
  // above will run client-side
  return null;
}
